import { IconBox } from "@/assets/icons/geist/IconBox";
import { IconBoxLink } from "@/assets/icons/geist/IconBoxLink";
import { IconCode } from "@/assets/icons/geist/IconCode";
import { IconEdit } from "@/assets/icons/geist/IconEdit";
import { IconCheck } from "@/assets/icons/geist/IconCheck";
import { IconClose } from "@/assets/icons/geist/IconClose";
import { IconEllipsisHorizontal } from "@/assets/icons/geist/IconEllipsisHorizontal";
import { IconNewEnvironment } from "@/assets/icons/geist/IconNewEnvironment";
import { IconPlay } from "@/assets/icons/geist/IconPlay";
import { IconStop } from "@/assets/icons/geist/IconStop";
import { EnvironmentStatusDot } from "@/components/environments/EnvironmentStatusDot";
import { Button } from "@/components/flexkit/Button";
import { Collapsible, CollapsibleContent } from "@/components/flexkit/Collapsible";
import { Link } from "@/components/flexkit/Link";
import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";
import { SkeletonText } from "@/components/podkit/loading/Skeleton";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Tooltip } from "@/components/Tooltip";
import { useEnvironmentName } from "@/hooks/use-environment-name";
import { useBranch } from "@/hooks/use-branch";
import { useGetEditorForEnv } from "@/hooks/use-editor-for-env";
import { useEnvironmentsByProjects, type EnvironmentsGroup } from "@/hooks/use-environments-by-projects";
import { useOpenEditor } from "@/hooks/use-open-editor";
import { TrackLocations } from "@/hooks/use-segment";
import { type PlainEditor } from "@/queries/editor-queries";
import { useStartEnvironment, useStopEnvironment, type PlainEnvironment } from "@/queries/environment-queries";
import { CreateEnvironmentButton } from "@/routes/environments/create/CreateEnvironmentButton";
import { getDetailsURL } from "@/routes/environments/details-url";
import { EnvironmentActionsContextMenu } from "@/routes/environments/details/EnvironmentActionsContextMenu";
import { canOpen, environmentHasReachedStablePhase, showStart, showStop } from "@/routes/environments/phase";
import { formatError } from "@/utils/errors";
import { gitStatusChangesCount, gitStatusLabel } from "@/utils/git-status";
import type { PlainMessage } from "@bufbuild/protobuf";
import {
    EnvironmentPhase,
    UpdateEnvironmentRequest,
    UpdateEnvironmentRequest_Metadata,
    type EnvironmentGitStatus,
} from "gitpod-next-api/gitpod/v1/environment_pb";
import { useCallback, useEffect, useMemo, useState, type FC } from "react";
import { useParams } from "react-router-dom";
import { Input } from "@/components/podkit/forms/Input";
import { useUpdateEnvironment } from "@/queries/environment-queries";

export const SidebarEnvironmentList: FC = () => {
    const { data: envsByProject, isLoading } = useEnvironmentsByProjects({
        // For now we still want to show environments created by agents in the sidebar
        includeAgentCreatedEnvironments: true,
    });

    if (isLoading) {
        return null;
    }

    return (
        <div
            className="contents"
            data-testid="environments-list"
            data-track-location={TrackLocations.SidebarEnvironmentList}
        >
            {envsByProject.map((group) => (
                <Group group={group} key={`group-${group.projectId || group.repoFullName}`} />
            ))}
        </div>
    );
};

const Group: FC<{
    group: EnvironmentsGroup;
}> = ({ group }) => {
    const { environmentId } = useParams();
    const shouldOpen = useMemo(() => {
        if (environmentId) {
            if (group.environments.some((env) => env.id === environmentId)) {
                return true;
            }
        }
        return group.environments.some(
            (env) =>
                env.status?.phase && [EnvironmentPhase.RUNNING, EnvironmentPhase.STARTING].includes(env.status.phase),
        );
    }, [environmentId, group.environments]);
    const [openState, setOpenState] = useState<"open" | "closed">(shouldOpen ? "open" : "closed");

    /**
     * When the group's `shouldOpen` computed state changes to true, we want to open the group.
     */
    useEffect(() => {
        if (shouldOpen) {
            setOpenState("open");
        }
    }, [shouldOpen]);

    const toggleOpen = useCallback(() => {
        setOpenState((current) => (current === "open" ? "closed" : "open"));
    }, []);

    return (
        <Collapsible open={openState === "open"} className="flex flex-col">
            <div
                className={cn(
                    "group flex h-9 w-full flex-row content-center items-center justify-between rounded-lg hover:bg-surface-03",
                    "has-[>button:focus-visible]:outline has-[>button:focus-visible]:outline-1 has-[>button:focus-visible]:outline-offset-0 has-[>button:focus-visible]:outline-content-primary",
                )}
            >
                <button
                    onClick={toggleOpen}
                    className="mr-2 flex min-w-0 grow flex-row content-center items-center focus:ring-0"
                    aria-label="Toggle Environment Group"
                >
                    <div className="flex shrink-0 pl-2">
                        {group.project ? <IconBox state={openState} /> : <IconBoxLink state={openState} />}
                    </div>
                    <Tooltip content={group.repoFullName ?? group.project?.metadata?.name}>
                        <div className="truncate pl-2 text-base" translate="no">
                            {group.repoFullName ? (
                                <span className="[direction:rtl]">{group.repoFullName}</span>
                            ) : group.projectMissing ? (
                                "Deleted project"
                            ) : (
                                <SkeletonText size="base" className="w-32" ready={!!group.project}>
                                    {group.project?.metadata?.name || "Untitled project"}
                                </SkeletonText>
                            )}
                        </div>
                    </Tooltip>
                </button>
                {group.project && (
                    <div className="mr-2 flex items-center opacity-0 focus-within:opacity-100 group-hover:opacity-100">
                        <CreateEnvironmentButton
                            project={group.project}
                            LeadingIcon={IconNewEnvironment}
                            variant={"ghost"}
                            size={"tiny"}
                            className="border-0 text-content-secondary"
                            onCreateSuccess={() => setOpenState("open")}
                        />
                    </div>
                )}
            </div>

            <CollapsibleContent>
                <div className="mx-[1px] my-1 flex flex-col gap-1">
                    {group.environments.map((env) => (
                        <Env key={env.id} env={env} active={location.pathname.includes("/" + env.id)} />
                    ))}
                </div>
            </CollapsibleContent>
        </Collapsible>
    );
};

const Env: FC<{ env: PlainEnvironment; active?: boolean }> = (p) => {
    const { selectedEditor } = useGetEditorForEnv(p.env.id);
    const [isRenaming, setIsRenaming] = useState(false);
    const envName = useEnvironmentName(p.env);
    const [newName, setNewName] = useState(envName);
    const updateEnvironment = useUpdateEnvironment();
    const branchName = useBranch(p.env);
    const { toast } = useToast();

    const handleSave = useCallback(async () => {
        if (newName === envName) {
            setIsRenaming(false);
            return;
        }

        if (updateEnvironment.isPending) {
            return;
        }

        try {
            await updateEnvironment.mutateAsync({
                req: new UpdateEnvironmentRequest({
                    environmentId: p.env.id,
                    metadata: new UpdateEnvironmentRequest_Metadata({
                        name: newName,
                    }),
                }),
            });
            if (newName === "") {
                toast({ title: `Environment name removed` });
            } else {
                toast({ title: `Environment renamed to "${newName}"` });
            }
        } catch (err) {
            toast({
                title: "Failed to rename environment",
                description: formatError(err),
            });
        }
        setIsRenaming(false);
    }, [p.env.id, newName, envName, updateEnvironment, toast]);

    return (
        <EnvironmentActionsContextMenu
            environment={p.env}
            defaultEditor={selectedEditor}
            startRename={() => setIsRenaming(true)}
        >
            {isRenaming ? (
                <div
                    className={cn(
                        "group h-9 w-full rounded-lg px-2 py-[6px]",
                        "flex items-center justify-between",
                        p.active ? "bg-surface-secondary" : "hover:bg-surface-03",
                    )}
                >
                    <div className="flex min-w-0 items-center gap-2">
                        <EnvironmentStatusIndicator env={p.env} />
                        <EnvironmentNameEditor
                            newName={newName}
                            onChange={setNewName}
                            onCancel={() => {
                                setIsRenaming(false);
                                setNewName(envName);
                            }}
                            onSave={handleSave}
                        />
                    </div>
                </div>
            ) : (
                <Link
                    className={cn(
                        "group h-9 w-full select-none rounded-lg px-2 py-[6px]",
                        "flex items-center justify-between",
                        p.active ? "bg-surface-secondary" : "hover:bg-surface-03",
                    )}
                    to={getDetailsURL(p.env)}
                    aria-label="Open Environment Details"
                    reloadIfFrontendUpdated={true}
                >
                    <div className="flex min-w-0 items-center gap-2">
                        <EnvironmentStatusIndicator env={p.env} />
                        <EnvironmentName name={envName} branchName={branchName} />
                    </div>

                    <div className="flex items-center gap-[2px]">
                        <div className="hidden group-hover:block">
                            <EnvironmentSidebarActions
                                env={p.env}
                                defaultEditor={selectedEditor}
                                onRename={() => setIsRenaming(true)}
                            />
                        </div>
                        <EnvironmentChanges git={p.env.status?.content?.git} />
                    </div>
                </Link>
            )}
        </EnvironmentActionsContextMenu>
    );
};

export const EnvironmentName: FC<{ name: string; branchName: string; size?: "sm" | "base" | "lg" }> = ({
    name,
    branchName,
    size = "base",
}) => {
    return (
        <SkeletonText size={size} className="w-32" ready={!!name}>
            {name === branchName && (
                <span
                    className={cn("truncate", {
                        "text-sm": size === "sm",
                        "text-base": size === "base",
                        "text-lg": size === "lg",
                    })}
                    translate="no"
                >
                    {name}
                </span>
            )}
            {name !== branchName && (
                <Tooltip content={"Branch: " + branchName}>
                    <span
                        className={cn("truncate", {
                            "text-sm": size === "sm",
                            "text-base": size === "base",
                            "text-lg": size === "lg",
                        })}
                        translate="no"
                    >
                        {name}
                    </span>
                </Tooltip>
            )}
        </SkeletonText>
    );
};

export const EnvironmentNameEditor: FC<{
    newName: string;
    onChange: (value: string) => void;
    onSave: () => void;
    onCancel: () => void;
}> = ({ newName, onChange, onSave, onCancel }) => {
    return (
        <form
            onSubmit={(e) => {
                e.preventDefault();
                onSave();
            }}
            onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === "Escape") {
                    e.preventDefault();
                    onCancel();
                }
            }}
            onBlur={(e: React.FocusEvent<HTMLFormElement>) => {
                // Check if the new focused element is a child of the form or if the document has focus
                const relatedTarget = e.relatedTarget as HTMLElement | null;
                const form = e.currentTarget;
                if (document.hasFocus() && (!relatedTarget || !form.contains(relatedTarget))) {
                    onSave();
                }
            }}
            className="flex items-center gap-0.5"
        >
            <Input
                id="name"
                className="h-8"
                value={newName}
                maxLength={128}
                onChange={(value) => {
                    onChange(value.target.value);
                }}
                autoFocus={true}
            />

            <EnvironmentSidebarRenameActions onSave={onSave} onCancel={onCancel} />
        </form>
    );
};

export const EnvironmentChanges: FC<{ git?: PlainMessage<EnvironmentGitStatus> } & PropsWithClassName> = ({
    git,
    className,
}) => {
    const changes = gitStatusChangesCount(git) ?? 0;

    if (changes === 0) {
        return null;
    }

    const tooltip = gitStatusLabel(git) ?? "";
    const label = changes > 99 ? "+99" : `${changes}`;
    return (
        <Tooltip content={tooltip}>
            <span className={cn("rounded-[10px] bg-surface-03 px-[6px] py-[2px] text-sm font-bold", className)}>
                {label}
            </span>
        </Tooltip>
    );
};

const EnvironmentSidebarActions: FC<{
    env: PlainEnvironment;
    defaultEditor: PlainEditor | undefined;
    onRename: () => void;
}> = ({ env, defaultEditor, onRename }) => {
    const opendEditor = useOpenEditor();
    // const renameEnv = useRenameEnvironment();
    const opendEditorButtonReady = canOpen(env) && !!defaultEditor;

    return (
        <div className="flex items-center gap-[2px]">
            <Tooltip content={`Rename`}>
                <Button
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onRename();
                    }}
                    LeadingIcon={IconEdit}
                    size="tiny"
                    variant="ghost"
                    aria-label={`Rename`}
                    className="border-0 text-content-secondary"
                />
            </Tooltip>
            {opendEditorButtonReady && (
                <Tooltip content={`Open in ${defaultEditor?.name}`}>
                    <Button
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            void opendEditor(defaultEditor, env);
                        }}
                        LeadingIcon={IconCode}
                        size="tiny"
                        variant="ghost"
                        aria-label={`Open in ${defaultEditor?.name}`}
                        className="border-0 text-content-secondary"
                    />
                </Tooltip>
            )}
            <Tooltip content="Open, logs, and more">
                <Button
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        // Slighly hacky way to trigger the context menu further up the DOM tree
                        e.target.dispatchEvent(
                            new MouseEvent("contextmenu", {
                                bubbles: true,
                                cancelable: true,
                                clientX: e.clientX,
                                clientY: e.clientY,
                                button: 2, // Right mouse button
                            }),
                        );
                    }}
                    LeadingIcon={IconEllipsisHorizontal}
                    size="tiny"
                    variant="ghost"
                    aria-label="Open, logs, and more"
                    className="border-0 text-content-secondary"
                />
            </Tooltip>
        </div>
    );
};

const EnvironmentSidebarRenameActions: FC<{
    onSave: () => void;
    onCancel: () => void;
}> = ({ onSave, onCancel }) => {
    return (
        <div className="flex items-center gap-0.5">
            <Tooltip content={`Save`}>
                <Button
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onSave();
                    }}
                    LeadingIcon={IconCheck}
                    size="tiny"
                    variant="ghost"
                    aria-label={`Save`}
                    className="border-0 text-content-secondary"
                />
            </Tooltip>
            <Tooltip content={`Cancel`}>
                <Button
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onCancel();
                    }}
                    LeadingIcon={IconClose}
                    size="tiny"
                    variant="ghost"
                    aria-label={`Cancel`}
                    className="border-0 text-content-secondary"
                />
            </Tooltip>
        </div>
    );
};

export const EnvironmentStatusIndicator: FC<{ env: PlainEnvironment }> = ({ env }) => {
    const startEnvironment = useStartEnvironment();
    const stopEnvironment = useStopEnvironment();
    const { toast } = useToast();

    const handleStart = useCallback(
        async (e: React.SyntheticEvent) => {
            e.stopPropagation();
            e.preventDefault();
            try {
                await startEnvironment.mutateAsync(env.id);
            } catch (error) {
                toast({
                    title: "Failed to start environment",
                    description: formatError(error),
                });
            }
        },
        [startEnvironment, env.id, toast],
    );

    const handleStop = useCallback(
        async (e: React.SyntheticEvent) => {
            e.stopPropagation();
            e.preventDefault();
            try {
                await stopEnvironment.mutateAsync(env.id);
            } catch (error) {
                toast({
                    title: "Failed to stop environment",
                    description: formatError(error),
                });
            }
        },
        [stopEnvironment, toast, env.id],
    );

    const hasReachedStablePhase = environmentHasReachedStablePhase(env);
    return (
        <div className="min-w-6">
            <EnvironmentStatusDot size="lg" env={env} className={cn({ "group-hover:hidden": hasReachedStablePhase })} />
            <div
                className={cn("hidden size-6", {
                    "group-hover:block": hasReachedStablePhase,
                })}
            >
                {hasReachedStablePhase && (
                    <>
                        {showStart(env) && (
                            <Tooltip content="Start Environment">
                                <Button
                                    size="tiny"
                                    variant="ghost"
                                    LeadingIcon={IconPlay}
                                    onClick={handleStart}
                                    loading={startEnvironment.isPending}
                                    aria-label="Start Environment"
                                    className="size-6 border-0 pt-0.5 text-content-secondary"
                                />
                            </Tooltip>
                        )}
                        {showStop(env) && (
                            <Tooltip content="Stop Environment">
                                <Button
                                    size="tiny"
                                    variant="ghost"
                                    LeadingIcon={IconStop}
                                    onClick={handleStop}
                                    loading={stopEnvironment.isPending}
                                    aria-label="Stop Environment"
                                    className="size-6 border-0 pt-0.5 text-content-secondary"
                                />
                            </Tooltip>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};
